import React, { useCallback, useMemo, useState } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { Entity } from "@common/types/entity";
import { FinancialAccount } from "@common/types/financialAccount";
import { getAccountNameWithLast4 } from "@common/helpers/financialAccount";
import { MergeFinancialAccountsResponse } from "@common/dto/financialAccount/mergeFinancialAccounts/merge-financial-accounts-response.dto";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { Picker } from "@design-system/Picker/Picker.lib";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { AccountPickerIcon } from "@components/financialAccount/AccountPickerItem";
import { useAccountMergingMutation } from "@mutations/financialAccount";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ModalComponentProps } from "@helpers/modal";
import { Button } from "@design-system/Button/Button";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Field } from "@design-system/Field/Field";
import { VStack } from "@design-system/VStack/VStack";
import { CountInput } from "@design-system/CountInput/CountInput";
import { useTypedFlags } from "@hooks/useTypedFlags";
import styles from "./MergeAccountsModal.module.scss";
import { MergePreview } from "./MergePreview";
interface Props extends ModalComponentProps {
    financialAccount: FinancialAccount;
    entity: Entity;
}

export const MergeAccountsModal: React.FC<Props> = ({
    financialAccount,
    entity,
    close,
}) => {
    const otherAccounts = useFinancialAccounts({ entityId: entity.id });
    const [accountToMerge, setAccountToMerge] = useState(financialAccount);
    const [destinationAccount, setDestinationAccount] =
        useState<FinancialAccount>();
    const accountsById = useMemo(
        () => new Map(otherAccounts.map((a) => [a.id, a])),
        [otherAccounts],
    );

    const selectableAccounts = useMemo(
        () =>
            otherAccounts.filter((account) => account.id !== accountToMerge.id),
        [otherAccounts, accountToMerge],
    );
    const accountItems = useMemo(
        () =>
            selectableAccounts.map((account) => ({
                value: account.id,
                label: getAccountNameWithLast4(account, 2),
                leading: <AccountPickerIcon account={account} />,
            })),
        [selectableAccounts],
    );

    const onSelectAccount = useCallback(
        (accountId: number | null) => {
            setDestinationAccount(
                accountId !== null ? accountsById.get(accountId) : undefined,
            );
        },
        [accountsById],
    );
    const [mergePreview, setMergePreview] =
        useState<MergeFinancialAccountsResponse>();

    const mergedAccountName = getAccountNameWithLast4(accountToMerge, 2);
    const destinationAccountName =
        destinationAccount && getAccountNameWithLast4(destinationAccount, 2);

    const flipAccounts = useCallback(() => {
        if (destinationAccount) {
            setAccountToMerge(destinationAccount);
            setDestinationAccount(accountToMerge);
        }
    }, [accountToMerge, destinationAccount]);

    const mergeMutation = useAccountMergingMutation();
    const { advancedFinancialAccountMergeWindow } = useTypedFlags();
    const [dateMatchWindowDays, setDateMatchWindowDays] = useState(1);

    const previewMerge = useCallback(async () => {
        if (!destinationAccount) {
            return;
        }

        const response = await mergeMutation.mutateAsync({
            accountToMergeId: accountToMerge.id,
            destinationAccountId: destinationAccount.id,
            entityId: entity.id,
            dryRun: true,
            ...(advancedFinancialAccountMergeWindow
                ? { dateMatchWindowDays }
                : {}),
        });

        setMergePreview(response);
    }, [
        destinationAccount,
        mergeMutation,
        accountToMerge.id,
        entity.id,
        advancedFinancialAccountMergeWindow,
        dateMatchWindowDays,
    ]);

    return (
        <div className={styles.mergeAccountsModal}>
            <StandardModalHeader>Merge accounts</StandardModalHeader>
            <StandardModalBody>
                {mergePreview && destinationAccount ? (
                    <MergePreview
                        destinationAccount={destinationAccount}
                        accountToMerge={accountToMerge}
                        mergePreview={mergePreview}
                        entity={entity}
                        dateMatchWindowDays={
                            advancedFinancialAccountMergeWindow
                                ? dateMatchWindowDays
                                : undefined
                        }
                        onReset={() => setMergePreview(undefined)}
                        onFinish={close}
                    />
                ) : (
                    <>
                        <VStack gap={16}>
                            <Field label="Merge">
                                <div className={styles.accountToMerge}>
                                    <AccountCard
                                        account={accountToMerge}
                                        showType={false}
                                        numberInName
                                    />
                                </div>
                            </Field>
                            <Field
                                label="into"
                                control="button"
                                className={styles.compactTrail}
                            >
                                <Picker.Select
                                    items={accountItems}
                                    searchable
                                    value={destinationAccount?.id ?? null}
                                    onValueChange={onSelectAccount}
                                    renderValue={() =>
                                        destinationAccount ? (
                                            <AccountCard
                                                account={destinationAccount}
                                                showType={false}
                                                numberInName
                                            />
                                        ) : undefined
                                    }
                                    placeholder="Select account"
                                    searchPlaceholder="Search..."
                                    emptyLabel="No matching accounts"
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            size="xl"
                                        />
                                    }
                                />
                            </Field>
                        </VStack>

                        {destinationAccount && (
                            <>
                                <HStack justify="center" className="mb-4">
                                    <Button
                                        variant="primary"
                                        size="md"
                                        onClick={flipAccounts}
                                    >
                                        Flip accounts
                                    </Button>
                                </HStack>

                                {advancedFinancialAccountMergeWindow && (
                                    <Field
                                        label="Match duplicates within (days)"
                                        className={styles.compactTrail}
                                    >
                                        <CountInput
                                            value={dateMatchWindowDays}
                                            min={1}
                                            onValueChange={(value) =>
                                                setDateMatchWindowDays(
                                                    Math.min(
                                                        30,
                                                        Math.max(1, value),
                                                    ),
                                                )
                                            }
                                        />
                                    </Field>
                                )}

                                <p>
                                    This will move all transactions from{" "}
                                    <strong>{mergedAccountName}</strong> into{" "}
                                    <strong>{destinationAccountName}</strong>{" "}
                                    and remove{" "}
                                    <strong>{mergedAccountName}</strong>
                                </p>

                                <footer className="text-right">
                                    <ButtonWithLoader
                                        variant="primary"
                                        size="md"
                                        loading={mergeMutation.isPending}
                                        onClick={previewMerge}
                                    >
                                        Preview merge
                                    </ButtonWithLoader>
                                    <p className="small mb-0 mt-2">
                                        This will not modify any data yet
                                    </p>
                                </footer>
                            </>
                        )}
                    </>
                )}
            </StandardModalBody>
        </div>
    );
};
